import { AuthUtils } from 'src/services/Auth/Auth.utils';
import { roles } from 'src/api/services/RoleClient';
import { useProfile } from 'src/models';
import { useDecoratedRequestContext } from 'src/features/requests/use-decorated-request-context';

/*
 * Read-only access for covering editors (#2653).
 *
 * #2653 opened revision reads to any editor holding a subscription assignment to
 * the company, so a covering editor can replay a brand's previously delivered
 * videos for styling reference. Every write on that request — commenting,
 * replying, reacting, deleting a revision, minting a share link — still belongs
 * to the editor it is assigned to, and the API enforces that. This mirrors the
 * same rule in the UI so those affordances are hidden rather than offered and
 * then rejected with a toast.
 *
 * Identity is read from the JWT first, the way `RenderForRole` does it, with the
 * profile cache as fallback. Neither source alone is enough, and the gap in each
 * one leaks writes rather than merely mislabelling the page — `MediaPolicy::
 * delete()` is an unconditional `return true`, so a revision-media delete
 * clicked inside either window actually lands:
 *
 * - `useProfile({ enabled: false })` is a cache read, so on a hard load straight
 *   onto a revisions URL it reports no user until the profile request resolves.
 *   The JWT is there synchronously on first render.
 * - `AuthUtils.getPayload()` returns null once the token is past `exp`, and
 *   nothing logs the user out at that moment — Http's interceptor refreshes and
 *   retries, so the click that follows succeeds. The profile stays cached across
 *   that window.
 *
 * Mirrors `RequestRevisionPolicy::canComment`: an editor is the owner when the
 * request carries their id. Admins and team leads are never read-only here —
 * the API lets them act on any request.
 *
 * Two deliberate divergences from the server rule:
 *
 * - It falls OPEN when the request has no editor, where `canComment` closes
 *   (`hasEditor($user)` is false for a null `editor_id`, unlike
 *   `doesBelongToEditor`). That is a real permission, not a cosmetic one:
 *   `RequestRevisionCommentPolicy::reply()`/`react()` gate on the company
 *   assignment a covering editor holds, and `TaskPolicy` is open to any
 *   non-customer, so those writes land. It is still the right default — an
 *   unassigned request is claimable, `doesBelongToEditor` treats it that way,
 *   and every status-change path lets any company editor pick it up. Locking a
 *   legitimate editor out of a claimable request would be the worse error.
 * - It ignores `hasEditorOnAnyOutcome`, because outcomes arrive without their
 *   `editor` relation. In practice this costs nothing: only leaf requests carry
 *   revisions, and on an outcome's own page `request.editor` is that outcome's
 *   editor.
 */
/**
 * The signed-in user's id and role, JWT first with the profile cache as
 * fallback. Exported because any gate comparing the viewer against
 * `request.editor?.id` needs the same two-source read — a profile-only `id` is
 * `undefined` on first paint, which silently makes "is this mine?" answer no.
 */
const useCurrentUserIdentity = (): { id?: string; type?: string } => {
  const payload = AuthUtils.getPayload();
  const profile = useProfile({ enabled: false });

  return {
    id: payload?.user_id ?? profile.id,
    type: payload?.user_type ?? profile.type,
  };
};

/**
 * Whether the signed-in user is looking at a request they may read but not
 * touch. See the note at the top of this file for the full rationale.
 */
const useEditorReadOnlyAccess = (): boolean => {
  const { id, type } = useCurrentUserIdentity();
  const request = useDecoratedRequestContext();

  if (type !== roles.editor || !id || !request?.editor?.id) {
    return false;
  }

  return request.editor.id !== id;
};

export { useEditorReadOnlyAccess, useCurrentUserIdentity };
